import { browserLogger } from '@newfront-insurance/dd-rum';
import { useCallback, useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import type { EmailGroup, SharedInbox } from '../components/outlook-mailbox-settings';

export interface UseChatSettingsResult {
  groups: EmailGroup[];
  selectedGroupIds: Set<string>;
  sharedInboxes: SharedInbox[];
  isLoading: boolean;
  error: string | null;
  toggleGroup: (groupId: string) => void;
  addSharedInbox: (email: string) => boolean;
  removeSharedInbox: (id: string) => void;
  toggleSharedInbox: (id: string) => void;
  refreshGroups: () => Promise<void>;
}

export interface UseChatSettingsConfig {
  graphClient: unknown;
  /** Server-persisted group IDs (from useNovaSettings via context) */
  serverSelectedGroupIds?: string[];
  /** Server-persisted shared inbox emails (from useNovaSettings via context) */
  serverSharedInboxes?: string[];
  /** Callback to persist changes to the server */
  onPersistSelectedGroups?: (groupIds: string[]) => void;
  onPersistSharedInboxes?: (emails: string[]) => void;
}

/**
 * Convert server-persisted email strings to SharedInbox objects for the UI.
 */
function emailsToSharedInboxes(emails: string[]): SharedInbox[] {
  return emails.map((email) => ({ id: uuidv4(), email, enabled: true }));
}

/**
 * Extract emails from SharedInbox objects for server persistence.
 */
function sharedInboxesToEmails(inboxes: SharedInbox[]): string[] {
  return inboxes.map((inbox) => inbox.email);
}

export function useChatSettings({
  graphClient,
  serverSelectedGroupIds,
  serverSharedInboxes,
  onPersistSelectedGroups,
  onPersistSharedInboxes,
}: UseChatSettingsConfig): UseChatSettingsResult {
  const [groups, setGroups] = useState<EmailGroup[]>([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState<Set<string>>(() => new Set(serverSelectedGroupIds ?? []));
  const [sharedInboxes, setSharedInboxes] = useState<SharedInbox[]>(() =>
    emailsToSharedInboxes(serverSharedInboxes ?? []),
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const hasFetchedRef = useRef(false);

  // Sync from server settings when they arrive
  useEffect(() => {
    if (serverSelectedGroupIds != null) {
      setSelectedGroupIds(new Set(serverSelectedGroupIds));
    }
    if (serverSharedInboxes != null) {
      setSharedInboxes(emailsToSharedInboxes(serverSharedInboxes));
    }
  }, [serverSelectedGroupIds, serverSharedInboxes]);

  // Mark as initialized on mount
  useEffect(() => {
    setIsInitialized(true);
  }, []);

  const fetchGroups = useCallback(async () => {
    if (!graphClient) {
      setError('Graph client not available');
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const client = graphClient as {
        api: (path: string) => {
          select: (fields: string) => { get: () => Promise<{ value: Record<string, string | boolean>[] }> };
        };
      };
      const response = await client
        .api('/me/memberOf')
        .select('id,displayName,mail,deletedDateTime,isArchived,mailEnabled,hideFromOutlookClients')
        .get();

      const emailGroups: EmailGroup[] = response.value
        .filter(
          (group: Record<string, string | boolean>) =>
            group.mailEnabled &&
            group.mail &&
            !group.hideFromOutlookClients &&
            !group.isArchived &&
            !group.deletedDateTime,
        )
        .map((group) => ({
          id: group.id as string,
          displayName: (group.displayName as string) || 'Unnamed Group',
          mail: group.mail as string,
        }));

      setGroups(emailGroups);

      browserLogger.debug('[Chat Settings] Fetched groups', { count: emailGroups.length });
    } catch (err) {
      browserLogger.error('[Chat Settings] Error fetching groups', { error: err });
      setError('Failed to load email groups');
    } finally {
      setIsLoading(false);
    }
  }, [graphClient]);

  // Fetch from API only if we don't have cached data
  useEffect(() => {
    if (isInitialized && groups.length === 0 && !hasFetchedRef.current) {
      hasFetchedRef.current = true;
      fetchGroups();
    }
  }, [isInitialized, groups.length, fetchGroups]);

  // Toggle group selection
  const toggleGroup = useCallback(
    (groupId: string) => {
      setSelectedGroupIds((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(groupId)) {
          newSet.delete(groupId);
        } else {
          newSet.add(groupId);
        }

        const newArray = Array.from(newSet);
        onPersistSelectedGroups?.(newArray);
        browserLogger.debug('[Chat Settings] Updated selection', { groupIds: newArray });
        return newSet;
      });
    },
    [onPersistSelectedGroups],
  );

  const addSharedInbox = useCallback(
    (email: string): boolean => {
      const trimmedEmail = email.trim().toLowerCase();

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(trimmedEmail)) {
        return false;
      }

      if (sharedInboxes.some((inbox) => inbox.email === trimmedEmail)) {
        return false;
      }

      const newInbox: SharedInbox = {
        id: uuidv4(),
        email: trimmedEmail,
        enabled: true,
      };

      const updatedInboxes = [...sharedInboxes, newInbox];
      setSharedInboxes(updatedInboxes);
      onPersistSharedInboxes?.(sharedInboxesToEmails(updatedInboxes));
      browserLogger.debug('[Chat Settings] Added shared inbox', { email: trimmedEmail });
      return true;
    },
    [sharedInboxes, onPersistSharedInboxes],
  );

  const removeSharedInbox = useCallback(
    (id: string) => {
      const updatedInboxes = sharedInboxes.filter((inbox) => inbox.id !== id);
      setSharedInboxes(updatedInboxes);
      onPersistSharedInboxes?.(sharedInboxesToEmails(updatedInboxes));
      browserLogger.debug('[Chat Settings] Removed shared inbox', { id });
    },
    [sharedInboxes, onPersistSharedInboxes],
  );

  const toggleSharedInbox = useCallback(
    (id: string) => {
      const updatedInboxes = sharedInboxes.map((inbox) =>
        inbox.id === id ? { ...inbox, enabled: !inbox.enabled } : inbox,
      );
      setSharedInboxes(updatedInboxes);
      onPersistSharedInboxes?.(sharedInboxesToEmails(updatedInboxes));
      browserLogger.debug('[Chat Settings] Toggled shared inbox', { id });
    },
    [sharedInboxes, onPersistSharedInboxes],
  );

  const refreshGroups = useCallback(async () => {
    await fetchGroups();
  }, [fetchGroups]);

  return {
    groups,
    selectedGroupIds,
    sharedInboxes,
    isLoading,
    error,
    toggleGroup,
    addSharedInbox,
    removeSharedInbox,
    toggleSharedInbox,
    refreshGroups,
  };
}
